export type WidgetChartType = 'bar' | 'line' | 'pie' | 'table' | 'metric' | 'card_grid' | 'opinions' | 'text' | 'map' | 'word_cloud';

/**
 * Data passed to the onPointClick callback when a chart element is clicked.
 * - label: human-readable name (series name for line/bar, slice name for pie, column name for table)
 * - key:   the primary variable — X-axis category value (line/bar), slice name (pie), or first-column value (table)
 * - value: the numeric or string value of the clicked element
 */
export interface WidgetClickPoint {
  label: string;
  key: string;
  value: string | number;
  /** All columns from the clicked row, available as {column_name} in URL templates */
  extra?: Record<string, string | number | boolean | null>;
}

/**
 * Defines an action triggered when a chart element is clicked.
 * URL templates may contain {key}, {label}, and {value} placeholders
 * which will be replaced with the corresponding values from the clicked point.
 * Example: "https://example.com/details?id={key}"
 */
export interface WidgetClickAction {
  type: 'url' | 'set_filter';
  /** URL template for type='url'. May contain {key}, {label}, {value}, {column_name} placeholders. */
  url: string;
  target?: '_blank' | '_self';
  /** For type='set_filter': the filter parameter name to set. */
  parameterName?: string;
  /** For type='set_filter': value template, e.g. '{key}'. Defaults to '{key}' if omitted. */
  valueTemplate?: string;
}

/**
 * Maps query result columns to chart roles.
 * When omitted, charts fall back to positional logic (columns[0] = x, columns[1] = y, etc.).
 */
export interface ColumnMapping {
  /** Bar/Line: X-axis column */
  xAxis?: string;
  /** Bar/Line: Y-axis value column */
  yAxis?: string;
  /** Bar/Line: each unique value becomes a separate series (grouping column) */
  key?: string;
  /** Display label column (used in legends/tooltips instead of raw key value) */
  label?: string;
  /** Pie / word cloud: slice name or word column */
  name?: string;
  /** Pie / word cloud: slice value or word frequency column */
  value?: string;
  /** Metric only: column containing the main numeric value */
  mainValue?: string;
  /** Metric only: column containing a pre-computed trend percentage (overrides auto-compute) */
  trendPercent?: string;
}

/** Configuration specific to the Metric (single-value card) widget type. */
export interface MetricConfig {
  /**
   * Number of days used for the trend window and sparkline range (default: 7).
   * Sync this with the SQL parameter (e.g. {{trend_days}}) via widget defaultValues.
   */
  trendRange?: number;
  /** Custom label after the trend percentage. Defaults to "last {trendRange} days". */
  trendLabel?: string;
  /**
   * How to derive the main value from a time-series result (default: "last").
   * - "last" — value of the final row
   * - "sum"  — sum of all rows
   * - "avg"  — average of all rows
   */
  mainValueMode?: 'last' | 'sum' | 'avg';
  /** Sparkline line color (hex). Defaults to the first palette color. */
  sparklineColor?: string;
  /** Force show or hide the sparkline. Default: auto (shown when multiple rows exist). */
  showSparkline?: boolean;
  /** String prepended to the main value (e.g. "$"). */
  valuePrefix?: string;
  /** String appended to the main value (e.g. "%"). */
  valueSuffix?: string;
}

export interface TableConfig {
  /**
   * Freeze columns up to and including this 1-based column number.
   * e.g. 1 = freeze only the first data column, 2 = freeze first two, etc.
   * Omit or set to 0 for no frozen columns.
   */
  freezeColumnIndex?: number;
  /** Freeze the header row (sticky top, default: true) */
  freezeHeader?: boolean;
  /** Freeze the footer row (sticky bottom) */
  freezeFooter?: boolean;
  /** Show aggregate footer row */
  showFooter?: boolean;
  /**
   * Client-side rows per page (default: 10).
   * Ignored when the top-level WidgetChartConfig.pageSize (server-side) is set —
   * in that case the server controls page size and this field has no effect.
   */
  pageSize?: number;
  /**
   * Map of 1-based column number to hex color string for column highlighting.
   * e.g. { "1": "#2563eb", "4": "#dc2626" } colors column 1 and column 4.
   * JSON keys must be quoted strings even though the values are column numbers.
   */
  columnColors?: Record<number, string>;
  /**
   * Explicit column display order by column name.
   * e.g. ["status", "name", "date"] — listed columns appear first in that order,
   * any remaining columns follow in their original order.
   */
  columnOrder?: string[];
  /**
   * Initial column widths as a percentage (0–100) of the table container width, by column name.
   * e.g. { "id": 5, "url": 8, "description": 40 }
   * Users can also resize columns by dragging header borders at runtime.
   */
  columnWidths?: Record<string, number>;
}

export interface CardGridMetricDef {
  label: string;
  col: string;
  format?: 'compact' | 'number';
  /** Optional hint shown via an info icon next to the metric label (e.g. explaining how a "rating" is calculated). */
  tooltip?: string;
}

export interface CardGridConfig {
  dateCol?: string;
  /** Optional end date column — when set, renders a date range: "dateCol — dateEndCol" */
  dateEndCol?: string;
  titleCol?: string;
  descriptionCol?: string;
  /** Column whose value is a JSON array string, e.g. "[52,49,94,70]", used for the per-card sparkline. */
  sparklineCol?: string;
  /** Label shown in the sparkline tooltip (e.g. "Messages"). Defaults to the column name if omitted. */
  sparklineLabel?: string;
  sparklineColor?: string;
  metrics?: CardGridMetricDef[];
  showDescription?: boolean;
  showMetrics?: boolean;
  /**
   * URL template opened when a card is clicked. Use {column_name} placeholders.
   *
   * Inner (app page):  "/threats/{id}"  — relative path, values are URL-encoded
   * Outer (external):  "{url}"          — when the column already holds a full URL
   *                                        (http/https), it is passed through as-is
   *
   * e.g. "/topic_reports/{narrative_id}/events/{id}"
   *      "https://example.com/posts/{slug}"
   *      "{source_url}"  (column contains the original article / post URL)
   */
  clickUrlTemplate?: string;
  /** Link target (default: "_blank") */
  clickTarget?: '_blank' | '_self';
  /** Column containing topics — JSON array string or comma-separated values. */
  topicsCol?: string;
  /** Column containing tags — JSON array string or comma-separated values. */
  tagsCol?: string;
  /** Show/hide the badges row (topics + tags). Default: true when topicsCol or tagsCol is set. */
  showBadges?: boolean;
  /**
   * Column containing a sentiment label. Accepted values (case-insensitive):
   * "positive" | "negative" | "neutral" | "mostly_positive" | "mostly_negative" | "controversial"
   *
   * For events: use opinion_balance->>'sentiment' in SQL (already returns the right values).
   * For narratives: write a CASE expression in SQL that returns one of the accepted string values.
   */
  sentimentCol?: string;
  /** Number of cards shown per page. Default: 9. */
  cardsPerPage?: number;
  /**
   * Tint each card's background with a washed-out color matching its sentiment.
   * Requires sentimentCol. Default: false.
   */
  sentimentBackground?: boolean;
  /**
   * CSS background color applied to every card, e.g. "#FEF9C3" or "rgba(59,131,246,0.08)".
   * Low-alpha rgba values are recommended so cards stay readable in the dark theme.
   */
  cardBackground?: string;
  /**
   * Column whose value is a CSS color used as that card's background. Rows with an
   * empty/NULL value fall back to cardBackground (or the default). Produce via a SQL
   * CASE expression, e.g. CASE WHEN severity = 'high' THEN '#FEE2E2' END AS card_color.
   */
  cardBackgroundCol?: string;
}

export interface OpinionsConfig {
  /** Column for the card title (auto-detected from column name when omitted) */
  titleCol?: string;
  /** Column with the opinion's sentiment, e.g. "positive" | "neutral" | "negative" (case-insensitive). Selects the sentiment icon shown on each card. */
  sentimentCol?: string;
  /** Metric definitions shown at the bottom of each card (e.g. Rating, Publications, Actors) */
  metrics?: CardGridMetricDef[];
  /** Show/hide the metrics row on each card (default: true) */
  showMetrics?: boolean;
  /**
   * URL template opened when a card is clicked. Use {column_name} placeholders.
   *
   * Inner (app page):  "/topic_reports/{narrative_id}/events/{event_id}"  — relative path, values are URL-encoded
   * Outer (external):  "{url}"  — when the column already holds a full URL (http/https), it is passed through as-is
   */
  clickUrlTemplate?: string;
  /** Link target (default: "_blank") */
  clickTarget?: '_blank' | '_self';
  /** Number of cards shown per page. Default: 9. */
  cardsPerPage?: number;
  /**
   * Tint each card's background with a washed-out color matching its sentiment.
   * Requires sentimentCol. Default: false.
   */
  sentimentBackground?: boolean;
}

export interface WidgetChartConfig {
  colors?: string[];
  /** Use sentiment color palette (positive/neutral/negative/other) instead of the default palette */
  useSentimentColors?: boolean;
  labels?: { x?: string; y?: string; title?: string };
  legend?: { position: 'top' | 'bottom' | 'left' | 'right' | 'none' };
  pieHole?: number;
  showGrid?: boolean;
  /** Bar chart only — stack all series into a single bar per category (default: false = grouped) */
  stacked?: boolean;
  /** Bar/line chart only — use logarithmic scale on the value axis (useful when values span several orders of magnitude) */
  logScale?: boolean;
  /** Bar chart only — enable ECharts brush selection + dataZoom toolbox for interactive range zooming */
  brushEnabled?: boolean;
  /** Bar chart only — enable axis break on the value axis. Pre-define breaks or leave initialBreaks empty and brush the chart to draw breaks interactively. */
  axisBreak?: { initialBreaks?: Array<{ start: number; end: number; gap?: string }> };
  /** Bar chart only — bar orientation: "horizontal" (default) or "vertical" */
  barDirection?: 'horizontal' | 'vertical';
  /** Bar chart only — show a slider + inside dataZoom for panning/zooming when there are many categories (default: false) */
  dataZoom?: boolean;
  echartsOption?: Record<string, unknown>;
  clickAction?: WidgetClickAction;
  /** Column-to-role mapping for chart data */
  columnMapping?: ColumnMapping;
  /** Table widget settings (used by TanstackChartTable) */
  tableConfig?: TableConfig;
  /**
   * Table only — enables server-side pagination with this many rows per page.
   * When set, the query must contain `LIMIT {{__limit__}} OFFSET {{__offset__}}`.
   * Takes precedence over tableConfig.pageSize (client-side), which is ignored.
   */
  pageSize?: number;
  /** Metric widget display settings. */
  metricConfig?: MetricConfig;
  /** Card grid widget display settings. */
  cardGridConfig?: CardGridConfig;
  /** Opinions widget display settings. */
  opinionsConfig?: OpinionsConfig;
  /** Text widget display settings. */
  textConfig?: TextConfig;
  /** World map widget display settings. */
  mapConfig?: MapConfig;
  /** Word / emoji cloud widget display settings. */
  wordCloudConfig?: WordCloudConfig;
  /** Cache lifetime in seconds; 0 or absent disables caching. */
  cache_ttl?: number;
  cache_type?: 'blocking' | 'stale_while_revalidate' | 'background_update';
  /** Seconds between automatic refreshes for `background_update`; defaults to `cache_ttl`. */
  cache_refresh_interval?: number;
}

export type WordCloudMode = 'keywords' | 'emojis' | 'both';

export interface WordCloudConfig {
  /**
   * Which tokens to show. 'both' (default) mixes keywords and emojis; the viewer can
   * still switch between the three modes with the in-widget toggle (see showModeToggle).
   */
  mode?: WordCloudMode;
  /**
   * Column that explicitly marks each row as 'keyword' or 'emoji'. When omitted, the
   * widget detects emojis from the token text itself.
   */
  kindColumn?: string;
  /** Show the Keywords / Emojis / Both toggle to viewers. Default: true. */
  showModeToggle?: boolean;
  /** Start as the cloud or as the ranked list; viewers can switch. Default: 'cloud'. */
  view?: 'cloud' | 'list';
  /** Show the Cloud / List switch to viewers. Default: true. */
  showViewToggle?: boolean;
  /** Let viewers hide words (right-click in the cloud, the hide button in the list). Default: true. */
  allowHide?: boolean;
  /**
   * Column with each token's relative change against the previous period:
   * 0.4 = +40%, -0.25 = -25%, NULL = unknown. Enables trend colouring.
   */
  trendColumn?: string;
  /**
   * Column with each token's net sentiment from -1 (all negative) to 1 (all positive),
   * or a label "positive" / "neutral" / "negative". NULL = unknown. Enables sentiment colouring.
   */
  sentimentColumn?: string;
  /** Net sentiment needed before a token counts as positive or negative. Default: 0.15. */
  sentimentThreshold?: number;
  /**
   * What colour encodes when the widget loads. Default: 'trend' when the data has one,
   * then 'sentiment', otherwise 'frequency'. Viewers can switch (see showColorToggle).
   */
  colorBy?: 'frequency' | 'trend' | 'sentiment';
  /** Show the "Color" selector to viewers when the data supports more than one encoding. Default: true. */
  showColorToggle?: boolean;
  /** Change needed before a token counts as rising or falling. Default: 0.3 (30%). */
  trendThreshold?: number;
  /** What the trend is compared with, shown next to the percentage. Default: "vs last week". */
  trendLabel?: string;
  /**
   * Column holding the number of messages the counts are based on (the same value in
   * every row). Shown as the caption "Based on the latest N messages".
   */
  sampleColumn?: string;
  /** Caption text; replaces the automatic one. */
  caption?: string;
  /** Maximum number of tokens drawn (highest values win). Default: 100. */
  maxWords?: number;
  /** Drop keywords shorter than this many characters. Default: 2. Does not apply to emojis. */
  minWordLength?: number;
  /** Case-insensitive list of keywords to hide (e.g. stop words the SQL did not remove). */
  stopWords?: string[];
  /**
   * Font size range in px as [min, max]. Default: fitted to the widget size and word count.
   * These are layout sizes: words are drawn about 10% smaller to keep gaps between them.
   */
  sizeRange?: [number, number];
  /**
   * Smallest font size for emojis in px. Emojis are single glyphs with lower counts than
   * keywords, so they get a higher floor than words. Default: 45% of the top size, between 20 and 32.
   */
  minEmojiSize?: number;
  /** Layout shape. Default: 'circle'. */
  shape?: 'circle' | 'cardioid' | 'diamond' | 'triangle-forward' | 'triangle' | 'pentagon' | 'star';
  /** Allow words to be rotated ±90°. Default: false. */
  rotate?: boolean;
  /** Layout grid step in px; larger values space the words out. Default: 8. */
  gridSize?: number;
}

/** Backend floor for `background_update` refreshes: each one re-runs the widget SQL. */
export const MIN_BACKGROUND_UPDATE_INTERVAL_SECONDS = 3600;

/** True when a `background_update` widget would refresh more often than the backend allows. */
export const isBackgroundUpdateIntervalTooShort = (config: WidgetChartConfig): boolean => {
  if (config.cache_type !== 'background_update') return false;
  const interval = config.cache_refresh_interval ?? config.cache_ttl;
  return typeof interval === 'number' && interval < MIN_BACKGROUND_UPDATE_INTERVAL_SECONDS;
};

export interface TextConfig {
  /** Markdown content to render. */
  content?: string;
  /** Background color (CSS value). Default: transparent. */
  backgroundColor?: string;
  /** Text color (CSS value). Default: inherit from theme. */
  textColor?: string;
}

export interface MapConfig {
  /**
   * Which map to render. 'world' (default) joins rows by ISO 3166-1 alpha-2
   * country codes; 'ukraine' / 'germany' / 'russia' show that country's
   * regions with neighbor countries as a gray backdrop and join rows by
   * ISO 3166-2 region codes (e.g. 'UA-63', 'DE-BY', 'RU-MOW'). The Russia map
   * covers the 83 internationally recognized federal subjects — Crimea,
   * Sevastopol and the occupied Ukrainian oblasts are shown as part of Ukraine.
   * country codes; 'ukraine' / 'germany' / 'russia' show that country's
   * regions with neighbor countries as a gray backdrop and join rows by
   * ISO 3166-2 region codes (e.g. 'UA-63', 'DE-BY', 'RU-MOW'). The Russia map
   * covers the 83 internationally recognized federal subjects — Crimea,
   * Sevastopol and the occupied Ukrainian oblasts are shown as part of Ukraine.
   */
  mapType?: 'world' | 'ukraine' | 'germany' | 'russia';
  /**
   * Russia map only: administrative level to render. 'subjects' (default) —
   * 83 federal subjects joined by ISO 3166-2 codes; 'districts' — 8 federal
   * districts joined by RU-CFO-style codes (RU-CFO, RU-SZFO, RU-YUFO, RU-SKFO,
   * RU-PFO, RU-UFO, RU-SFO, RU-DFO).
   */
  federalLevel?: 'subjects' | 'districts';
  /** Russia map only: include the Kaliningrad exclave. Default: true. */
  showKaliningrad?: boolean;
  /**
   * Show city name markers on regional maps. Default: true, or false when
   * labelColumn is set (city names clash with on-region labels); an explicit
   * value always wins. Ignored for 'world'.
   */
  showCities?: boolean;
  /**
   * Column whose raw value is drawn as a label on each region or country
   * (e.g. "5.8%", a number, any string). Shapes without data get no label.
   * Works on all map types including 'world'.
   */
  labelColumn?: string;
  /**
   * Column with sentiment values (positive | negative | neutral |
   * mostly_positive | mostly_negative | controversial). Fills regions or
   * countries with the app's sentiment colors, overriding the choropleth
   * gradient — the visualMap legend is hidden and valueColumn only feeds the
   * tooltip. Unrecognized values leave the shape at emptyColor. Works on all
   * map types including 'world'.
   */
  sentimentColumn?: string;
  /**
   * Show the sentiment color legend (bottom-left card) when sentimentColumn
   * is set. Lists only sentiments present in the data. Default: true.
   */
  showSentimentLegend?: boolean;
  /** Background (sea) color on regional maps. Default: light blue-gray. Ignored for 'world'. */
  seaColor?: string;
  /**
   * Optional data value → region/country code mapping applied before the join,
   * e.g. { "Group Alpha": "DE-BY" }. Values mapped to the same code are summed.
   */
  regionMapping?: Record<string, string>;
  /** SQL column with ISO 3166-1 alpha-2 country codes (e.g. 'US', 'DE'). Falls back to first column. */
  countryColumn?: string;
  /** Numeric column used for choropleth fill intensity. Omit for a presence map (all data countries share one color). */
  valueColumn?: string;
  /** Columns shown in the hover tooltip, in order. Falls back to all non-country columns. */
  tooltipColumns?: string[];
  /** Display label override per column, e.g. { "pub_count": "Publications" }. Falls back to the column name. */
  tooltipLabels?: Record<string, string>;
  /** Two-stop gradient for the choropleth: [low-color, high-color]. Default: light-to-dark blue. */
  colorRange?: [string, string];
  /** Fill color for countries that have no data. Default: light gray. */
  emptyColor?: string;
  /** Enable scroll-wheel zoom and drag-to-pan. Default: true. */
  roam?: boolean;
  /** Show rank (#N of M) in the hover tooltip when valueColumn is set. Default: false. */
  showRank?: boolean;
  /** Initial map center as [longitude, latitude], e.g. [15, 52] for Europe. */
  center?: [number, number];
  /** Initial zoom level (1 = world view). Higher values zoom in, e.g. 3 for a continent. */
  zoom?: number;
}

export interface WidgetSQLQuery {
  sql: string;
  lastExecutedAt?: string;
}

export interface WidgetDataRow {
  [column: string]: string | number | boolean | null;
}

export interface WidgetDataResult {
  columns: string[];
  rows: WidgetDataRow[];
  executionTimeMs?: number;
  error?: string | null;
  totalRows?: number | null;
  cached?: boolean;
}

export type FilterType = 'select' | 'multi_select' | 'date_range' | 'text';

export interface DashboardFilter {
  id: string;
  dashboardId: string;
  widgetId: string | null;        // null = dashboard-level, set = widget-level
  title: string;
  parameterName: string;
  filterType: FilterType;
  querySql: string;               // SQL to populate options
  // Whether `querySql` reflects the real value (true) or is still the lazy-load
  // placeholder `''` (false/undefined). The dashboard GET omits filter SQL, so
  // filters start unloaded and only become loaded via getFilter (eager-load on
  // edit start, or the filter editor opening). This distinguishes "SQL not yet
  // loaded" from "SQL deliberately cleared" — both look like '' otherwise — so
  // draft overrides are only sent for filters whose SQL we actually know.
  querySqlLoaded?: boolean;
  defaultValue: any | null;
  config: Record<string, any>;
  order: number;
  hidden: boolean;                // hidden filters are not shown in UI but can be set via URL/click actions
}

export interface FilterOption {
  [key: string]: string | number | boolean | null;
}

export interface FilterOptionsResult {
  filterId: string;
  parameterName: string;
  options: FilterOption[];
  error?: string | null;
}

export interface CreateFilterDTO {
  title: string;
  parameterName: string;
  filterType: FilterType;
  querySql: string;
  widgetId?: string | null;
  defaultValue?: any | null;
  config?: Record<string, any>;
  order?: number;
  hidden?: boolean;
}

export interface UpdateFilterDTO {
  title?: string;
  parameterName?: string;
  filterType?: FilterType;
  querySql?: string;
  widgetId?: string | null;
  defaultValue?: any | null;
  config?: Record<string, any>;
  order?: number;
  hidden?: boolean;
}

export interface Widget {
  id: string;
  dashboardId: string;
  title: string;
  chartType: WidgetChartType;
  query: WidgetSQLQuery;
  config: WidgetChartConfig;
  defaultValues: Record<string, any>;
  data: WidgetDataResult | null;
}

export interface DashboardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
}

export interface Dashboard {
  id: string;
  title: string;
  description?: string;
  widgets: Widget[];
  layout: DashboardLayoutItem[];
  filters: DashboardFilter[];
}

export interface CreateDashboardDTO {
  title: string;
  description?: string;
  filters?: CreateFilterDTO[];
}

export interface UpdateDashboardDTO {
  title?: string;
  description?: string;
  layout?: DashboardLayoutItem[];
}

export interface CreateWidgetDTO {
  dashboardId: string;
  title: string;
  chartType: WidgetChartType;
  query?: WidgetSQLQuery;
  config?: WidgetChartConfig;
  defaultValues?: Record<string, any>;
}

export interface UpdateWidgetDTO {
  title?: string;
  chartType?: WidgetChartType;
  query?: WidgetSQLQuery;
  config?: WidgetChartConfig;
  defaultValues?: Record<string, any>;
  data?: WidgetDataResult | null;
}

export interface WidgetTemplate {
  type: WidgetChartType;
  label: string;
  description: string;
  defaultConfig: WidgetChartConfig;
  sampleQuery: string;
}

export interface DashboardState {
  dashboards: Dashboard[];
  totalDashboards: number;
  currentPage: number;
  pageSize: number;
  activeDashboard: Dashboard | null;
  draftDashboard: Dashboard | null;
  snapshotDashboard: Dashboard | null;
  hasUnsavedChanges: boolean;
  isEditing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  selectedWidgetId: string | null;
  filterValues: Record<string, any>;
  filterOptions: Record<string, FilterOptionsResult>;
}

export type DashboardAction =
  | { type: 'SET_DASHBOARDS'; dashboards: Dashboard[]; total: number; page: number; size: number }
  | { type: 'SET_ACTIVE_DASHBOARD'; dashboard: Dashboard | null }
  | { type: 'SET_EDITING'; isEditing: boolean }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'START_EDITING'; draft: Dashboard; snapshot: Dashboard }
  | { type: 'CLEAR_DRAFT' }
  | { type: 'SET_DRAFT'; draft: Dashboard }
  | { type: 'DRAFT_ADD_WIDGET'; widget: Widget; layoutItem: DashboardLayoutItem }
  | { type: 'DRAFT_UPDATE_WIDGET'; widgetId: string; updates: Partial<Widget> }
  | { type: 'DRAFT_REMOVE_WIDGET'; widgetId: string }
  | { type: 'DRAFT_UPDATE_LAYOUT'; layout: DashboardLayoutItem[] }
  | { type: 'DRAFT_SET_WIDGET_DATA'; widgetId: string; data: WidgetDataResult }
  | { type: 'SET_WIDGET_DATA'; widgetId: string; data: WidgetDataResult }
  | { type: 'SELECT_WIDGET'; widgetId: string | null }
  | { type: 'ADD_DASHBOARD'; dashboard: Dashboard }
  | { type: 'DELETE_DASHBOARD'; dashboardId: string }
  | { type: 'SET_FILTER_VALUE'; parameterName: string; value: any }
  | { type: 'SET_FILTER_VALUES'; values: Record<string, any> }
  | { type: 'SET_FILTER_OPTIONS'; options: Record<string, FilterOptionsResult> }
  | { type: 'CLEAR_FILTER_VALUES' }
  | { type: 'DRAFT_ADD_FILTER'; filter: DashboardFilter }
  | { type: 'DRAFT_UPDATE_FILTER'; filterId: string; updates: Partial<DashboardFilter> }
  | { type: 'DRAFT_REMOVE_FILTER'; filterId: string }
  | { type: 'HYDRATE_FILTER_SQL'; sqlByFilterId: Record<string, string> };
